// src/pages/profile-settings/patient/Notifications.jsx
import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import SettingsScreenShell from "@/sot/layouts/SettingsScreenShell.jsx";
import TopBarCenter from "@/sot/primitives/TopBarCenter.jsx";
import ListItem48 from "@/sot/primitives/ListItem48.jsx";
import AfriPill from "@/sot/primitives/AfriPill.jsx";
import Card from "@/components/ui/Card.jsx";
import supabase from "@/services/supabaseClient";
import {
  deriveReferralNotificationTarget,
  deriveMessageNotificationTarget,
  isMessageNotification,
} from "@/utils/notificationTargets.js";

const FILTER_KEYS = ["all", "urgent", "unread", "appointments"];
const ICON_EMPTY = "/icons/appointments/date_range.svg";
const ICON_MESSAGE = "/icons/messages_active.svg";

const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;

const monthKey = (d, locale = undefined) =>
  d.toLocaleString(locale, { month: "long", year: "numeric" });

const dayLabel = (d, locale = undefined, t = (key, fallback) => fallback) => {
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const weekday = d.toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "short",
  });
  const time = d.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });
  return isToday
    ? `${t("notifications.common.today", "Today")} ${time}`
    : `${weekday} ${time}`;
};

function safeLower(value) {
  return String(value || "").trim().toLowerCase();
}

function maybeJson(value) {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function deriveAppointmentId(row) {
  if (isMessageNotification(row)) return null;
  if (safeLower(row?.type).startsWith("payment_reversal:")) return null;
  const direct = row?.appointment_id || row?.appointmentId || null;
  if (direct) return String(direct);

  const payload = maybeJson(row?.message);
  if (payload && typeof payload === "object") {
    const candidate = payload.appointment_id || payload.appointmentId || payload.id;
    if (candidate) return String(candidate);
  }

  const sources = [row?.type, row?.title, row?.message].map((value) => String(value || ""));
  for (const source of sources) {
    const match = source.match(UUID_RE);
    if (match?.[0]) return match[0];
  }
  return null;
}

function isAppointmentNotification(row, appointmentId) {
  if (appointmentId) return true;
  const type = safeLower(row?.type);
  const title = safeLower(row?.title);
  const message = safeLower(row?.message);
  return (
    type.startsWith("payment_reversal:") ||
    type.includes("appointment") ||
    title.includes("appointment") ||
    message.includes("appointment") ||
    message.includes("consultation")
  );
}

function isUrgentNotification(row) {
  const priority = safeLower(row?.priority);
  if (priority === "urgent") return true;
  const haystack = [row?.type, row?.title, row?.message]
    .map((part) => safeLower(part))
    .join(" ");
  return (
    haystack.includes("urgent") ||
    haystack.includes("critical") ||
    haystack.includes("emergency")
  );
}

function iconForRow(row, kind, urgent) {
  if (isMessageNotification(row)) return ICON_MESSAGE;
  if (urgent) return "/icons/list-items/notifications_clock.svg";

  const text = `${safeLower(row?.title)} ${safeLower(row?.message)} ${safeLower(row?.type)}`;
  if (kind === "appointments") {
    if (text.includes("reminder")) return "/icons/list-items/notifications_reminder.svg";
    if (text.includes("new")) return "/icons/list-items/new_appointment.svg";
    return "/icons/list-items/notifications_alarm.svg";
  }
  return "/icons/list-items/notifications_alarm.svg";
}

function appointmentTitle(row, t) {
  const type = safeLower(row?.type);
  if (type.startsWith("payment_reversal:")) {
    return type.includes(":refunded:")
      ? t("notifications.payment.titleRefunded", "Payment refunded")
      : t("notifications.payment.titleAttention", "Payment reversal needs attention");
  }
  const rescheduleState = type.includes("appointment_reschedule_request:")
    ? (type.endsWith(":accepted") ? "accepted" : type.endsWith(":cancelled") ? "cancelled" : "requested")
    : "";
  const cancellationState = type.includes("appointment_cancellation_request:")
    ? (type.endsWith(":accepted") ? "accepted" : type.endsWith(":cancelled") ? "cancelled" : "requested")
    : "";
  if (type.startsWith("appointment_ehr_access_request:")) {
    return t("notifications.headers.ehrAccessRequest", "EHR access request");
  }
  if (rescheduleState) {
    return t("notifications.headers.appointmentUpdate", "Appointment update");
  }
  if (type.startsWith("appointment_reschedule_approved:")) {
    return t("notifications.headers.newTimeRequestApproved", "New time request approved");
  }
  if (type.startsWith("appointment_reschedule_declined:")) {
    return t("notifications.headers.newTimeRequestDeclined", "New time request declined");
  }
  if (cancellationState === "requested") {
    return t("notifications.headers.cancellationRequestSent", "Cancellation request sent");
  }
  if (cancellationState === "cancelled") {
    return t("notifications.headers.cancellationRequestDeclined", "Cancellation request declined");
  }
  if (cancellationState === "accepted") {
    return t("notifications.headers.appointmentCancelled", "Appointment cancelled");
  }
  if (type.startsWith("appointment_cancellation_declined:")) {
    return t(
      "notifications.headers.cancellationRequestDeclined",
      "Cancellation request declined",
    );
  }
  if (type.startsWith("appointment_cancelled:")) {
    return t("notifications.headers.appointmentCancelled", "Appointment cancelled");
  }
  if (type.startsWith("appointment_declined:")) {
    return t("notifications.headers.appointmentDeclined", "Appointment declined");
  }
  if (type.startsWith("appointment_confirmed:") || type.startsWith("appointment_")) {
    return t("notifications.headers.upcomingAppointment", "Upcoming appointment");
  }
  return (
    String(row?.title || t("notifications.common.notification", "Notification")).trim() ||
    t("notifications.common.notification", "Notification")
  );
}

function appointmentDescription(row, t) {
  const type = safeLower(row?.type);
  if (type.startsWith("payment_reversal:")) {
    return type.includes(":refunded:")
      ? t("notifications.payment.patientRefunded", "Your payment has been refunded to the original payment method.")
      : t("notifications.payment.attention", "The cancelled appointment has a payment reversal requiring platform attention.");
  }
  const rescheduleState = type.includes("appointment_reschedule_request:")
    ? (type.endsWith(":accepted") ? "accepted" : type.endsWith(":cancelled") ? "cancelled" : "requested")
    : "";
  const cancellationState = type.includes("appointment_cancellation_request:")
    ? (type.endsWith(":accepted") ? "accepted" : type.endsWith(":cancelled") ? "cancelled" : "requested")
    : "";
  if (type.startsWith("appointment_ehr_access_request:")) {
    return t(
      "notifications.messages.ehrAccessRequested",
      "Your doctor requested access to your health record for this appointment.",
    );
  }
  if (rescheduleState === "accepted") {
    return t(
      "notifications.messages.acceptedNewTime",
      "You accepted the new appointment time.",
    );
  }
  if (rescheduleState === "cancelled") {
    return t(
      "notifications.messages.declinedNewTime",
      "You declined the new appointment time.",
    );
  }
  if (rescheduleState === "requested") {
    return t(
      "notifications.messages.doctorProposedNewTime",
      "Your doctor proposed a new appointment time. Review and confirm the change.",
    );
  }
  if (type.startsWith("appointment_reschedule_approved:")) {
    return t(
      "notifications.messages.yourRescheduleApprovedGeneric",
      "Your new appointment time request was approved.",
    );
  }
  if (type.startsWith("appointment_reschedule_declined:")) {
    return t(
      "notifications.messages.yourRescheduleDeclinedGeneric",
      "Your new appointment time request was declined.",
    );
  }
  if (cancellationState === "requested") {
    return t(
      "notifications.messages.cancellationRequestSentGeneric",
      "Your request to cancel this appointment was sent.",
    );
  }
  if (cancellationState === "cancelled") {
    return t(
      "notifications.messages.yourCancellationDeclinedGeneric",
      "Your cancellation request was declined.",
    );
  }
  if (cancellationState === "accepted" || type.startsWith("appointment_cancelled:")) {
    return t(
      "notifications.messages.yourCancellationApprovedGeneric",
      "Your cancellation request was approved.",
    );
  }
  if (type.startsWith("appointment_cancellation_declined:")) {
    return t(
      "notifications.messages.yourCancellationDeclinedGeneric",
      "Your cancellation request was declined.",
    );
  }
  if (type.startsWith("appointment_declined:")) {
    return t(
      "notifications.messages.yourAppointmentDeclinedGeneric",
      "Your appointment request was declined.",
    );
  }
  if (type.startsWith("appointment_confirmed:") || type.startsWith("appointment_")) {
    return t(
      "notifications.messages.upcomingAppointmentGeneric",
      "Your appointment is scheduled.",
    );
  }
  return (
    String(row?.message || "").trim() ||
    t("notifications.common.openDetails", "Open to view details.")
  );
}

function normalizeNotification(row, t) {
  const createdAt = row?.created_at || row?.updated_at || new Date().toISOString();
  const date = new Date(createdAt);
  const appointmentId = deriveAppointmentId(row);
  const kind = isAppointmentNotification(row, appointmentId) ? "appointments" : "other";
  const urgent = isUrgentNotification(row);

  return {
    id: String(row?.id || ""),
    title:
      kind === "appointments"
        ? appointmentTitle(row, t)
        : String(row?.title || t("notifications.common.notification", "Notification")),
    desc:
      kind === "appointments"
        ? appointmentDescription(row, t)
        : String(row?.message || "").trim(),
    ts: createdAt,
    _date: Number.isNaN(date.getTime()) ? new Date() : date,
    read: Boolean(row?.read_at),
    urgent,
    kind,
    appointmentId,
    referralId: deriveReferralNotificationTarget(row)?.referralId || null,
    conversationId: deriveMessageNotificationTarget(row)?.conversationId || null,
    participantUserId: deriveMessageNotificationTarget(row)?.participantUserId || null,
    icon: iconForRow(row, kind, urgent),
  };
}

export default function Notifications() {
  const nav = useNavigate();
  const { languageCode, t } = useLanguage();
  const [activeFilter, setActiveFilter] = React.useState("all");
  const [activeId, setActiveId] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [errorMsg, setErrorMsg] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    async function loadRows() {
      try {
        setLoading(true);
        setErrorMsg("");

        const {
          data: { user },
          error: authErr,
        } = await supabase.auth.getUser();

        if (authErr || !user?.id) {
          if (alive) setRows([]);
          return;
        }

        const { data, error } = await supabase
          .from("notifications")
          .select("id, title, message, type, priority, read_at, created_at, updated_at")
          .eq("user_id", user.id)
          .order("updated_at", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(250);

        if (error) throw error;
        if (!alive) return;

        const unreadBellIds = (Array.isArray(data) ? data : [])
          .filter((row) => !isMessageNotification(row) && !row?.read_at)
          .map((row) => String(row?.id || "").trim())
          .filter(Boolean);

        if (unreadBellIds.length > 0) {
          const nowIso = new Date().toISOString();
          const { error: markReadError } = await supabase
            .from("notifications")
            .update({ read_at: nowIso })
            .in("id", unreadBellIds)
            .eq("user_id", user.id);

          if (!markReadError) {
            (Array.isArray(data) ? data : []).forEach((row) => {
              if (unreadBellIds.includes(String(row?.id || "").trim())) {
                row.read_at = nowIso;
              }
            });
            window.dispatchEvent(new Event("afrimed:notifications:changed"));
          }
        }

        const normalised = (Array.isArray(data) ? data : [])
          .filter((row) => !isMessageNotification(row))
          .map((row) => normalizeNotification(row, t))
          .filter((row) => row.id);

        setRows(normalised);
      } catch (err) {
        if (!alive) return;
        const msg = String(err?.message || "");
        if (/permission denied/i.test(msg) && /notifications/i.test(msg)) {
          // Soft-fail to empty inbox UX when RLS/grants are not yet open.
          setRows([]);
          setErrorMsg("");
          return;
        }
        setRows([]);
        setErrorMsg(t("notifications.common.errors.loadFailed", "Could not load notifications."));
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadRows();
    return () => {
      alive = false;
    };
  }, [t]);

  const counts = React.useMemo(() => {
    const all = rows.length;
    const urgent = rows.filter((n) => n.urgent).length;
    const unread = rows.filter((n) => !n.read).length;
    const appointments = rows.filter((n) => n.kind === "appointments").length;
    return { all, urgent, unread, appointments };
  }, [rows]);

  const chipItems = React.useMemo(
    () =>
      FILTER_KEYS.map((key) => ({
        value: key,
        label: `${t(`notifications.filters.${key}`, key)} (${counts[key] ?? 0})`,
      })),
    [counts, t]
  );

  const filtered = React.useMemo(() => {
    if (activeFilter === "all") return rows;
    if (activeFilter === "urgent") return rows.filter((n) => n.urgent);
    if (activeFilter === "unread") return rows.filter((n) => !n.read);
    if (activeFilter === "appointments") return rows.filter((n) => n.kind === "appointments");
    return rows;
  }, [activeFilter, rows]);

  React.useEffect(() => {
    if (!filtered.length) {
      setActiveId(null);
      return;
    }
    if (!activeId || !filtered.some((row) => row.id === activeId)) {
      setActiveId(filtered[0].id);
    }
  }, [filtered, activeId]);

  const grouped = React.useMemo(() => {
    const by = new Map();
    for (const row of filtered) {
      const key = monthKey(row._date, languageCode);
      if (!by.has(key)) by.set(key, []);
      by.get(key).push(row);
    }
    return [...by.entries()]
      .map(([key, arr]) => [key, arr.sort((a, b) => b._date - a._date)])
      .sort((a, b) => b[1][0]._date - a[1][0]._date);
  }, [filtered, languageCode]);

  return (
    <SettingsScreenShell>
      <TopBarCenter title={t("notifications.common.title", "Notifications")} onBack={() => nav(-1)} />

      <div className="mt-4 mb-2 chips-viewport -mx-1">
        <div className="chips-row px-1">
          {chipItems.map((chip) => (
            <AfriPill
              key={chip.value}
              value={chip.value}
              label={chip.label}
              isActive={chip.value === activeFilter}
              onChange={setActiveFilter}
              size="small"
              variant="notifications"
            />
          ))}
        </div>
      </div>

      <div className="mt-4">
        {loading && (
          <p className="text-center py-8 text-body-medium text-on-surface-var">
            {t("notifications.common.loading", "Loading notifications...")}
          </p>
        )}

        {!loading && !!errorMsg && (
          <p className="text-center py-8 text-body-medium text-on-surface-var">
            {errorMsg}
          </p>
        )}

        {!loading &&
          !errorMsg &&
          grouped.map(([month, items]) => (
            <section key={month} className="mb-10">
              <div className="h-px w-full bg-[rgba(24,50,119,0.10)] mb-5" />

              <div className="w-full text-center mb-4">
                <span className="text-title-large text-on-surface">{month}</span>
              </div>

              {items.map((n) => {
                const isActive = n.id === activeId;
                const to = `/settings/patient/notifications/${n.id}`;

                return (
                  <div key={n.id} className="mb-4">
                    <div className="text-body-medium text-on-surface-var mb-2">
                      {dayLabel(n._date, languageCode, t)}
                    </div>

                    <Link
                      to={to}
                      state={
                        n.conversationId
                          ? {
                              conversationId: n.conversationId,
                              participantUserId: n.participantUserId,
                            }
                          : n.referralId
                            ? { referralId: n.referralId }
                          : n.appointmentId
                            ? { appointmentId: n.appointmentId }
                            : null
                      }
                      className="block no-underline text-inherit"
                      onMouseDown={() => setActiveId(n.id)}
                      onFocus={() => setActiveId(n.id)}
                      onBlur={() => setActiveId(null)}
                    >
                      <Card
                        className={[
                          "px-4 py-3 transition-shadow",
                          isActive
                            ? "outline outline-1 outline-[rgba(24,50,119,0.14)]"
                            : "",
                        ].join(" ")}
                      >
                        <ListItem48
                          variant="bare"
                          icon={n.icon}
                          title={n.title}
                          subtitle={n.desc || t("notifications.common.openDetails", "Open to view details.")}
                          isActive={isActive}
                          titleClass="text-body-large text-on-surface"
                          subtitleClass="text-body-medium text-on-surface-var"
                        />
                      </Card>
                    </Link>
                  </div>
                );
              })}
            </section>
          ))}

        {!loading && !errorMsg && grouped.length === 0 && (
          <div className="min-h-[calc(100dvh-280px)] grid place-items-center">
            <div className="text-center">
              <img
                src={ICON_EMPTY}
                alt=""
                className="h-[76px] w-[76px] mx-auto"
                draggable="false"
              />
              <p className="mt-4 text-title-large text-on-surface">
                {t("notifications.common.emptyTitle", "No notifications to display")}
              </p>
              <p className="mt-2 text-body-large text-on-surface-var max-w-[360px] mx-auto">
                {t("notifications.common.emptyBody", "Your inbox is currently empty. Check back later!")}
              </p>
            </div>
          </div>
        )}
      </div>
    </SettingsScreenShell>
  );
}
